// src/components/dashboard/ClientDashboard.jsx - Complete with all sections
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';import emailjs from '@emailjs/browser';
import { loadStripe } from '@stripe/stripe-js';
import './client-dashboard.css';
import './overview.css';
import './dashboard-shell.css';
import './quotes.css';
import './customize.css';
import OverviewPanel from './OverviewPanel';
import { isSelfServe, bookingConfig } from '../services/servicePackages';
import logo3 from '../../assets/logo3.png';
import Services from '../services/Services';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_test_51Rt1scJWEk4lOKeyR0a5TgVOeCprciFTMNqG1R7o8pbASgFPlaEFu4k1TeDA8TJDFblZgftThifr4xDcX0xJZrPE00uDcW7w6e'
);

// TODO: replace with your real Cal.com / Calendly consultation link

const ClientDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [customizeCategory, setCustomizeCategory] = useState(null);
  const [lastPackageId, setLastPackageId] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  
  // Stats for overview
  const [stats, setStats] = useState({
    projects: 0,
    invoices: 0,
    pending: 0,
    messages: 0
  });
  
  const [paymentButtonLoading, setPaymentButtonLoading] = useState(false);

  // Custom package form states
  const [customFormData, setCustomFormData] = useState({
    projectType: '',
    description: '',
    timeline: '',
    budget: '',
    features: ''
  });
  const [customFormSubmitted, setCustomFormSubmitted] = useState(false);
  const [customFormLoading, setCustomFormLoading] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = ({ title, message, variant = 'success', duration = 3000 }) => {
    clearTimeout(toastTimerRef.current);
    setToast({ title, message, variant, open: true });
    toastTimerRef.current = setTimeout(() => {
      setToast((t) => (t ? { ...t, open: false } : null));
      setTimeout(() => setToast(null), 250);
    }, duration);
  };

  // Avatar picker state
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const avatarPickerRef = useRef(null);

  // Avatar options - DiceBear styles
  const avatarOptions = [
    { id: 'initials', type: 'initials', label: 'Initials' },
    { id: 'avatar1', type: 'dicebear', style: 'avataaars', seed: 'Felix' },
    { id: 'avatar2', type: 'dicebear', style: 'avataaars', seed: 'Aneka' },
    { id: 'avatar3', type: 'dicebear', style: 'avataaars', seed: 'Jasper' },
    { id: 'avatar4', type: 'dicebear', style: 'fun-emoji', seed: 'happy' },
    { id: 'avatar5', type: 'dicebear', style: 'fun-emoji', seed: 'cool' },
    { id: 'avatar6', type: 'dicebear', style: 'lorelei', seed: 'Felix' },
    { id: 'avatar7', type: 'dicebear', style: 'notionists', seed: 'business' },
    { id: 'avatar8', type: 'dicebear', style: 'personas', seed: 'developer' },
  ];

  // Get avatar URL
  const getAvatarUrl = (avatar) => {
    if (!avatar || avatar.type === 'initials') return null;
    return `https://api.dicebear.com/7.x/${avatar.style}/svg?seed=${avatar.seed}`;
  };

  // Handle avatar selection
  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    localStorage.setItem('userAvatar', JSON.stringify(avatar));
    setShowAvatarPicker(false);
    showToast({
      title: 'Avatar Updated!',
      message: 'Your profile picture has been changed.',
      variant: 'success'
    });
  };

  // Close avatar picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (avatarPickerRef.current && !avatarPickerRef.current.contains(event.target)) {
        setShowAvatarPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load saved avatar on mount
  useEffect(() => {
    const savedAvatar = localStorage.getItem('userAvatar');
    if (savedAvatar) {
      try {
        setSelectedAvatar(JSON.parse(savedAvatar));
      } catch (e) {
        console.error('Error parsing saved avatar');
      }
    }
  }, []);

  const [searchParams] = useSearchParams();

// Read tab from URL query parameter
useEffect(() => {
  const tabParam = searchParams.get('tab');
  if (tabParam && ['overview', 'customize', 'projects', 'invoices', 'quotes'].includes(tabParam)) {
    setActiveTab(tabParam);
  }
  const catParam = searchParams.get('cat');
  if (catParam && ['entrepreneur', 'smallbusiness', 'enterprise'].includes(catParam)) {
    setCustomizeCategory(catParam);
  }
}, [searchParams]);

  // Package selection reuses the shared <Services /> component (same cards as
  // the home page) — so there is no separate package list to maintain here.


  useEffect(() => {
    fetchDashboardData();
    fetchQuotes();
    // Carry over the package the visitor picked on the Services page.
    const stored = sessionStorage.getItem('selectedPackage') || sessionStorage.getItem('pendingPackage');
    if (stored) {
      const pkg = JSON.parse(stored);
      setSelectedPackage(pkg);
      setActiveTab('customize');
      sessionStorage.removeItem('selectedPackage');
      sessionStorage.removeItem('pendingPackage');
      sessionStorage.removeItem('fromServices');
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      // Fetch projects
      const projectsResponse = await fetch(`${apiUrl}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setProjects(projectsData.projects || []);
        // Update stats
        setStats(prev => ({ ...prev, projects: projectsData.projects?.length || 0 }));
      }
      
      // Fetch invoices
      const invoicesResponse = await fetch(`${apiUrl}/api/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (invoicesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        setInvoices(invoicesData.invoices || []);
        // Update stats
        setStats(prev => ({ 
          ...prev, 
          invoices: invoicesData.invoices?.length || 0,
          pending: invoicesData.invoices?.filter(i => i.status === 'pending').length || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleCustomFormChange = (e) => {
    setCustomFormData({
      ...customFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    setCustomFormLoading(true);
    
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      const response = await fetch(`${apiUrl}/api/quotes/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(customFormData)
      });
      
      if (response.ok) {
        setCustomFormSubmitted(true);
        showToast({
          title: 'Success',
          message: 'Your custom request has been submitted. We\'ll contact you shortly!',
          variant: 'success'
        });
      }
    } catch (error) {
      console.error('Error submitting custom form:', error);
      showToast({
        title: 'Error',
        message: 'Failed to submit request. Please try again.',
        variant: 'error'
      });
    } finally {
      setCustomFormLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await fetch(`${apiUrl}/api/quotes/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuotes(data.quotes || []);
      }
    } catch (err) {
      console.error('Failed to load quotes', err);
    }
  };

  const handlePayQuote = async (quoteId) => {
    try {
      setPayingId(quoteId);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const res = await fetch(`${apiUrl}/api/quotes/${quoteId}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      showToast({ title: 'Payment', message: data.error || 'Could not start checkout.', variant: 'error' });
    } catch (err) {
      showToast({ title: 'Payment', message: 'Could not start checkout.', variant: 'error' });
    } finally {
      setPayingId(null);
    }
  };

  // Fire-and-forget: log the booking intent so you have a record/brief even
  // before Cal confirms. Cal collects its own intake form + payment in the modal.
  const logBookingIntent = () => {
    try {
      const booking = bookingConfig(selectedPackage);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      fetch(`${apiUrl}/api/leads/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          name: getUserName(),
          email: user?.email,
          source: 'booking_request',
          message: `Booking started for "${selectedPackage?.name || 'Custom build'}" — ${booking.type === 'inperson' ? 'in-person meeting' : 'video call'}${booking.fee ? ` ($${booking.fee} booking fee)` : ' (free)'}.`,
          page: 'dashboard/customize'
        })
      }).catch(() => {});
    } catch (err) {
      /* non-blocking */
    }
  };

  // Open the right Cal.com event in a NEW TAB (logs the brief first). A new tab
  // (instead of an in-page embed) avoids loading Cal's embed.js — so no
  // cross-origin "Script error." and identical behaviour in dev and production.
  const openBooking = () => {
    logBookingIntent();
    const cal = bookingConfig(selectedPackage).calLink;
    window.open(`https://cal.com/${cal}`, '_blank', 'noopener,noreferrer');
  };

  const handleProceedToPayment = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          packageId: selectedPackage?.id,
          addOnIds: [],
          customerEmail: user?.email,
          paymentOption: 'deposit'
        })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.error === 'consultation_required') {
        // Edge case: server says this needs a consult. Log it and open Cal.
        openBooking();
        return;
      }
      showToast({ title: 'Checkout', message: data.error || 'Could not start checkout.', variant: 'error' });
    } catch (err) {
      console.error('Payment error:', err);
      alert('Could not reach the payment server.');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    const firstName = user?.firstName || user?.profile?.firstName || '';
    const lastName = user?.lastName || user?.profile?.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getUserName = () => {
    const firstName = user?.firstName || user?.profile?.firstName || '';
    const lastName = user?.lastName || user?.profile?.lastName || '';
    return `${firstName} ${lastName}`.trim() || user?.email || 'User';
  };

  // Overview Section (shared component — also used on the home page)
  const OverviewSection = () => (
    <OverviewPanel
      user={user}
      stats={stats}
      onNavigate={(tab, cat) => { setCustomizeCategory(cat ?? null); setActiveTab(tab); }}
    />
  );

  // Customize Package Section
  const CustomizeSection = () => (
    <div className="customize__section">
      {!selectedPackage ? (
        <Services
          embedded
          initialCategory={customizeCategory}
          focusPackageId={lastPackageId}
          onSelectPackage={(pkg) => {
            setSelectedPackage(pkg);
            if (pkg?.categoryId) setCustomizeCategory(pkg.categoryId);
            if (pkg?.id) setLastPackageId(pkg.id);
          }}
        />
      ) : (() => {
        const selfServe = isSelfServe(selectedPackage);
        const oneTime = selectedPackage.interval !== 'month';
        const price = selectedPackage.priceValue || 0;
        const deposit = Math.round(price * 0.5);
        const booking = bookingConfig(selectedPackage);
        const inPerson = booking.type === 'inperson';
        const bookingFee = booking.fee || 0;
        return (
          <div className="package__customization">
            <button
              className="btn btn--back"
              onClick={() => {
                setSelectedPackage(null);
              }}
            >
              <i className='uil uil-arrow-left'></i> Back to packages
            </button>

            <div className="pkg__detail">
              <span className="pkg__eyebrow">Selected package</span>
              <h3 className="pkg__name">{selectedPackage.name}</h3>
              <p className="pkg__desc">{selectedPackage.description}</p>
              {selectedPackage.delivery && (
                <p className="pkg__delivery">
                  <i className="uil uil-clock"></i> Typical delivery: {selectedPackage.delivery}
                </p>
              )}

              {selfServe ? (
                <>
                  <div className="pkg__price">
                    <span className="pkg__price-amt">${price.toLocaleString()}</span>
                    <span className="pkg__price-unit">{oneTime ? 'one-time' : '/ month'}</span>
                  </div>
                  {oneTime && (
                    <p className="pkg__deposit">
                      Start with a 50% deposit (${deposit.toLocaleString()}) — balance due on delivery.
                    </p>
                  )}
                  <div className="pkg__cta">
                    <button
                      className="btn btn--primary pkg__pay"
                      onClick={handleProceedToPayment}
                      disabled={loading}
                    >
                      <i className="uil uil-lock-alt"></i>
                      {loading
                        ? 'Redirecting…'
                        : oneTime
                          ? `Pay deposit · $${deposit.toLocaleString()}`
                          : `Subscribe · $${price.toLocaleString()}/mo`}
                    </button>
                    <a className="btn btn--ghost pkg__contact" href="/#contact">
                      <i className="uil uil-comment-alt-message"></i> Have a question? Contact me
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <p className="pkg__scope">
                    {inPerson ? (
                      <>For enterprise builds we meet in person to scope it properly. A <strong>${bookingFee} booking fee</strong> reserves your meeting — credited toward the project. You'll pick a time and fill a short brief next.</>
                    ) : bookingFee > 0 ? (
                      <>Let's scope this on a strategy call. A <strong>${bookingFee} booking fee</strong> holds your slot — credited toward the project. You'll pick a time and fill a short brief next.</>
                    ) : (
                      <>This one we scope together on a quick call — Zoom, no charge. Right after, I send you a fixed quote you can pay from your <strong>Quotes</strong> tab.</>
                    )}
                  </p>
                  <div className="pkg__cta">
                    <button
                      className="btn btn--primary pkg__book"
                      onClick={openBooking}
                    >
                      <i className={inPerson ? 'uil uil-map-marker' : 'uil uil-calendar-alt'}></i>
                      {inPerson
                        ? `Book in-person meeting · $${bookingFee}`
                        : bookingFee > 0
                          ? `Book a call · $${bookingFee} fee`
                          : 'Book a free call'}
                    </button>
                    <a className="btn btn--ghost pkg__contact" href="/#contact">
                      <i className="uil uil-comment-alt-message"></i> Prefer to message? Contact me
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
  // Projects Section
  const ProjectsSection = () => (
    <div className="projects__section">
      <h2 className="section__title">My Projects</h2>
      
      {projects.length === 0 ? (
        <div className="empty__state">
          <i className='uil uil-folder-open'></i>
          <h3>No Projects Yet</h3>
          <p>Start your first project by selecting a package</p>
          <button 
            className="btn btn--primary"
            onClick={() => setActiveTab('customize')}
          >
            Get Started
          </button>
        </div>
      ) : (
        <div className="projects__list">
          {projects.map((project, idx) => (
            <div key={idx} className="project__card">
              <div className="project__header">
                <h3>{project.name}</h3>
                <span className={`status status--${project.status}`}>
                  {project.status}
                </span>
              </div>
              <p className="project__description">{project.description}</p>
              <div className="project__footer">
                <span className="project__date">
                  Started: {new Date(project.createdAt).toLocaleDateString()}
                </span>
                <button className="btn btn--outline">View Details</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Quotes Section — admin-issued quotes the client can pay
  const QuotesSection = () => (
    <div className="quotes">
      <div className="quotes__head">
        <h2 className="quotes__title">Your quotes</h2>
        <p className="quotes__sub">
          Quotes we've prepared for you after our conversations — pay one to kick it off.
        </p>
      </div>

      {quotes.length === 0 ? (
        <div className="quotes__empty">
          <i className="uil uil-file-check-alt"></i>
          <p>No quotes yet. Once we've talked through your project, your quote shows up here, ready to pay.</p>
        </div>
      ) : (
        <div className="quotes__list">
          {quotes.map((q) => {
            const due = q.depositOnly ? q.amount * 0.5 : q.amount;
            return (
              <div key={q.id} className={`quote-card quote-card--${q.status}`}>
                <div className="quote-card__main">
                  <div className="quote-card__top">
                    <h3 className="quote-card__title">{q.title}</h3>
                    <span className={`quote-card__badge quote-card__badge--${q.status}`}>{q.status}</span>
                  </div>
                  {q.description && <p className="quote-card__desc">{q.description}</p>}
                  <div className="quote-card__meta">
                    <span className="quote-card__amount">${q.amount.toLocaleString()}</span>
                    {q.depositOnly && <span className="quote-card__deposit">50% deposit to start</span>}
                  </div>
                </div>
                <div className="quote-card__action">
                  {q.status === 'paid' ? (
                    <span className="quote-card__paid"><i className="uil uil-check-circle"></i> Paid</span>
                  ) : (
                    <button
                      className="quote-card__pay"
                      onClick={() => handlePayQuote(q.id)}
                      disabled={payingId === q.id}
                    >
                      <i className="uil uil-lock-alt"></i>
                      {payingId === q.id ? 'Redirecting…' : `Pay $${due.toLocaleString()}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Invoices Section
  const InvoicesSection = () => (
    <div className="invoices__section">
      <div className="section__header">
        <h2 className="section__title">Invoices</h2>
        <div className="filter__buttons">
          <button 
            className={invoiceFilter === 'all' ? 'active' : ''}
            onClick={() => setInvoiceFilter('all')}
          >
            All
          </button>
          <button 
            className={invoiceFilter === 'paid' ? 'active' : ''}
            onClick={() => setInvoiceFilter('paid')}
          >
            Paid
          </button>
          <button 
            className={invoiceFilter === 'pending' ? 'active' : ''}
            onClick={() => setInvoiceFilter('pending')}
          >
            Pending
          </button>
        </div>
      </div>
      
      {invoices.length === 0 ? (
        <div className="empty__state">
          <i className='uil uil-file-alt'></i>
          <h3>No Invoices Yet</h3>
          <p>Your invoices will appear here once you start a project</p>
        </div>
      ) : (
        <div className="invoices__table">
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices
                .filter(inv => invoiceFilter === 'all' || inv.status === invoiceFilter)
                .map((invoice, idx) => (
                  <tr key={idx}>
                    <td>#{invoice.number || `INV-00${idx + 1}`}</td>
                    <td>{new Date(invoice.date).toLocaleDateString()}</td>
                    <td>{invoice.description}</td>
                    <td>${invoice.amount.toLocaleString()}</td>
                    <td>
                      <span className={`status status--${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn--sm btn--outline">
                        <i className='uil uil-download-alt'></i> Download
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="client__dashboard">
      {/* Sticky top bar: header + tabs */}
      <div className="dashboard__topbar">
      {/* Header */}
      <header className="dashboard__header">
        <div className="header__left">
          <img src={logo3} alt="Logo" className="dashboard__logo" />
        </div>
        <div className="header__right">
          <div className="header__user">
            <div className="header__avatar">
              {selectedAvatar && selectedAvatar.type === 'dicebear' ? (
                <img src={getAvatarUrl(selectedAvatar)} alt="" />
              ) : (
                <span>{getInitials()}</span>
              )}
            </div>
            <div className="header__user-meta">
              <span className="header__hello">Welcome back</span>
              <span className="header__name">{getUserName()}</span>
            </div>
          </div>
          <Link to="/" className="home__button">
            <i className='uil uil-home'></i>
            Home
          </Link>
          <button className="logout__button" onClick={onLogout}>
            <i className='uil uil-sign-out-alt'></i>
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="dashboard__nav">
        <button
          className={`nav__tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className='uil uil-dashboard'></i>
          Overview
        </button>
        <button
          className={`nav__tab ${activeTab === 'customize' ? 'active' : ''}`}
          onClick={() => setActiveTab('customize')}
        >
          <i className='uil uil-cube'></i>
          Customize Package
        </button>
        <button
          className={`nav__tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          <i className='uil uil-folder'></i>
          My Projects
        </button>
        <button
          className={`nav__tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          <i className='uil uil-file-alt'></i>
          Invoices
        </button>
        <button
          className={`nav__tab ${activeTab === 'quotes' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotes')}
        >
          <i className='uil uil-file-check-alt'></i>
          Quotes
        </button>
      </nav>
      </div>

      {/* Main Content */}
      <main className="dashboard__content">
        {activeTab === 'overview' && <OverviewSection />}
        {activeTab === 'customize' && <CustomizeSection />}
        {activeTab === 'projects' && <ProjectsSection />}
        {activeTab === 'invoices' && <InvoicesSection />}
        {activeTab === 'quotes' && <QuotesSection />}
      </main>

      {/* Toast Notification */}
      {toast && toast.open && (
        <div className={`toast toast--${toast.variant}`}>
          <h4>{toast.title}</h4>
          <p>{toast.message}</p>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;